/**
 * Lightweight fuzzy/typo correction against the AI-search catalog's own
 * vocabulary (tool names + keywords). Exists specifically to catch real
 * user typos that neither MiniSearch's own fuzzy matching nor
 * matcher.js's exact-token synonym lookup catch -- confirmed directly,
 * not assumed: for the query "...go for singing it" (typo for
 * "signing"), MiniSearch's own top-20 results never included sign-pdf,
 * and matcher.js's synonym table only matches exact words, not
 * misspellings of them.
 *
 * Uses Damerau-Levenshtein distance (standard Levenshtein plus adjacent
 * transpositions counted as a single edit, not two substitutions) rather
 * than plain Levenshtein, and only corrects at distance <= 1. This was
 * deliberately tightened after testing found a real over-correction:
 * a looser version (distance <= 2 under plain Levenshtein) corrected
 * "trasfer" into "tracker" -- an unrelated word with no connection to
 * the user's actual intent, picked only because it happened to be the
 * closest vocabulary entry by raw edit count. Distance <= 1 under
 * Damerau-Levenshtein still catches the most common real typo patterns
 * (a single wrong/missing/extra letter, or two adjacent letters swapped
 * -- confirmed directly: "singing" is exactly one adjacent transposition
 * away from "signing") while being far less likely to land on an
 * unrelated word purely by coincidence.
 */
import { catalog } from "./catalog.js";

// Mirrors matcher.js's own stopword list (not imported, since it isn't
// exported there and duplicating a small constant is simpler than
// changing that file's exports). Excluding these matters here for a
// real reason found by testing, not just tidiness: "from" is common
// enough to show up in almost any query, and it sits exactly one
// adjacent-letter transposition away from "form" (a real vocabulary
// word, from form-filling tools) -- without this exclusion, "remove
// background from a photo" was getting corrected into "...form a
// photo", changing the query's actual meaning for a word that was
// never misspelled.
const STOPWORDS = new Set([
  "a", "an", "the", "and", "then", "or", "to", "of", "for", "with", "my",
  "please", "can", "you", "how", "do", "i", "me", "it", "is", "are", "on",
  "in", "at", "want", "need", "help", "make", "get", "use", "using", "into",
  "from", "some", "all", "this", "that", "what", "where", "when", "will",
  "would", "could", "should", "go", "then",
]);

let vocabulary: Set<string> | null = null;

function buildVocabulary(): Set<string> {
  if (vocabulary) return vocabulary;
  const words = new Set<string>();
  for (const tool of catalog) {
    for (const w of tool.name.toLowerCase().split(/\s+/)) {
      if (w.length >= 4) words.add(w);
    }
    for (const kw of tool.keywords) {
      const w = kw.toLowerCase();
      if (w.length >= 4) words.add(w);
    }
  }
  vocabulary = words;
  return words;
}

/** Damerau-Levenshtein distance (Levenshtein + adjacent transpositions as one edit). */
function damerauLevenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost, // substitution
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1); // adjacent transposition
      }
    }
  }
  return dp[m][n];
}

/**
 * Returns a corrected version of the query, replacing individual words
 * with a close vocabulary match when one exists. Returns the original
 * query unchanged if nothing needed correcting.
 */
export function fuzzyCorrectQuery(query: string): string {
  const vocab = buildVocabulary();
  const words = query.split(/\s+/);
  let changed = false;

  const corrected = words.map((raw) => {
    const w = raw.toLowerCase();
    if (w.length < 4 || vocab.has(w) || STOPWORDS.has(w)) return raw;

    for (const v of Array.from(vocab)) {
      if (Math.abs(v.length - w.length) > 1) continue; // cheap pre-filter -- distance 1 needs length within 1
      if (damerauLevenshtein(w, v) === 1) {
        changed = true;
        return v;
      }
    }
    return raw;
  });

  return changed ? corrected.join(" ") : query;
}
